import { Component } from '@theme/component';

class TWForms extends Component {
  constructor() {
    super();
    this.submitEndpoint = this.dataset.endpoint;
    this.products = [];
  }

  connectedCallback() {
    this.form = this.querySelector('[data-form]');
    this.addProductButton = this.querySelector('[data-add-product]');
    this.productsList = this.querySelector('[data-products-list]');
    this.productTemplate = this.querySelector('[data-product-template]');
    this.totalProductsEl = this.querySelector('[data-total-products]');
    this.statusEl = this.querySelector('[data-status]');
    this.submitButton = this.querySelector('[data-submit-button]');
    this.loadingEl = this.querySelector('[data-loading]');
    this.submitButtonLabel = this.submitButton?.textContent.trim();
    this.productsForm = this.querySelector('[data-products-form]');

    if (!this.form) return;
    this.imageWidgets = this.setupImageWidgets();
    this.autoFillWithURLParams();
    this.form.addEventListener('submit', this.handleSubmit);

    if (this.addProductButton) {
      this.addProductButton.addEventListener('click', this.handleAddProduct);
    }

    if (!this.submitEndpoint) {
      this.setStatus('Missing submission endpoint.', 'error');
    }
  }

  handleAddProduct = () => {
    if (!this.productsForm || !this.productTemplate || !this.productsList)
      return;

    const productInputs =
      this.productsForm.querySelectorAll('[data-form-input]');
    const productData = {};
    const productsObj = {};
    let hasValue = false;
    for (const input of productInputs) {
      const label = input.dataset.formInput || input.name;
      const name = input.name;
      const value = input.value?.trim();
      const altRequired = input.dataset.altRequired === 'true';
      if (altRequired && !value) {
        this.setStatus(`Please fill out the required field: ${label}`, 'error');
        return;
      }
      if (label) {
        productData[label] = value;
      }
      if (name) {
        productsObj[name] = value;
      }
      if (value) {
        hasValue = true;
      }
    }

    if (!hasValue) {
      this.setStatus(
        'Add at least one product field before adding a product.',
        'error',
      );
      return;
    }

    const productId = `${Date.now()}`;
    this.products.push({ id: productId, ...productsObj });
    this.addProductCard(productData, productId);
    this.updateTotalProducts();
    this.clearProductInputs(productInputs);
    this.setStatus('', 'info');
  };

  handleRemoveProduct = (event) => {
    const productId = event.currentTarget.dataset.productId;
    if (!productId) return;

    this.products = this.products.filter((product) => product.id !== productId);
    const productCard =
      event.currentTarget.closest('[data-product-card]') ||
      event.currentTarget.parentElement;
    if (productCard) {
      productCard.remove();
    }
    this.updateTotalProducts();
  };

  setupImageWidgets() {
    const widgets = [];
    this.querySelectorAll('[data-image-upload]').forEach((el) => {
      const widget = {
        el,
        name: el.dataset.name,
        label: el.dataset.formInput || el.dataset.name,
        maxFiles: parseInt(el.dataset.maxFiles || '3', 10),
        maxSizeMb: parseFloat(el.dataset.maxSizeMb || '5'),
        required: el.dataset.required === 'true',
        input: el.querySelector('[data-image-input]'),
        trigger: el.querySelector('[data-image-trigger]'),
        previews: el.querySelector('[data-image-previews]'),
        template: el.querySelector('[data-image-preview-template]'),
        files: [],
      };
      if (widget.trigger && widget.input) {
        widget.trigger.addEventListener('click', () => widget.input.click());
        widget.input.addEventListener('change', (event) =>
          this.handleImageSelection(widget, event),
        );
      }
      widgets.push(widget);
    });
    return widgets;
  }

  handleImageSelection = (widget, event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = '';
    if (!selected.length) return;

    const allowed = ['image/png', 'image/jpeg', 'image/webp'];
    for (const file of selected) {
      if (widget.files.length >= widget.maxFiles) {
        this.setStatus(
          `You can upload a maximum of ${widget.maxFiles} images for ${widget.label}.`,
          'error',
        );
        break;
      }
      if (!allowed.includes(file.type)) {
        this.setStatus(`${file.name} is not a supported image type.`, 'error');
        continue;
      }
      if (file.size > widget.maxSizeMb * 1024 * 1024) {
        this.setStatus(
          `${file.name} is larger than ${widget.maxSizeMb}MB.`,
          'error',
        );
        continue;
      }
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const previewUrl = URL.createObjectURL(file);
      widget.files.push({ id, file, previewUrl });
      this.addImagePreview(widget, { id, file, previewUrl });
      this.setStatus('', 'info');
    }
  };

  addImagePreview(widget, item) {
    if (!widget.previews || !widget.template) return;
    const node = widget.template.content.cloneNode(true);
    const card = node.querySelector('[data-image-card]');
    const thumb = node.querySelector('[data-image-thumb]');
    const nameEl = node.querySelector('[data-image-name]');
    const removeBtn = node.querySelector('[data-image-remove]');
    if (card) card.dataset.imageId = item.id;
    if (thumb) {
      thumb.src = item.previewUrl;
      thumb.alt = item.file.name;
    }
    if (nameEl) nameEl.textContent = item.file.name;
    if (removeBtn) {
      removeBtn.addEventListener('click', () =>
        this.removeImage(widget, item.id),
      );
    }
    widget.previews.appendChild(node);
  }

  removeImage(widget, id) {
    const item = widget.files.find((entry) => entry.id === id);
    if (item) URL.revokeObjectURL(item.previewUrl);
    widget.files = widget.files.filter((entry) => entry.id !== id);
    const card = widget.previews?.querySelector(`[data-image-id="${id}"]`);
    if (card) card.remove();
  }

  resetImageWidgets() {
    if (!this.imageWidgets) return;
    this.imageWidgets.forEach((widget) => {
      widget.files.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      widget.files = [];
      if (widget.previews) widget.previews.innerHTML = '';
    });
  }

  async buildImagePayload(widget) {
    const results = [];
    for (const item of widget.files) {
      results.push(await this.compressImage(item.file));
    }
    return results;
  }

  compressImage(file, { maxDimension = 1600, quality = 0.7 } = {}) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        let { width, height } = img;
        if (width >= height && width > maxDimension) {
          height = Math.round((height * maxDimension) / width);
          width = maxDimension;
        } else if (height > width && height > maxDimension) {
          width = Math.round((width * maxDimension) / height);
          height = maxDimension;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas is not supported.'));
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Image compression failed.'));
              return;
            }
            const reader = new FileReader();
            reader.onload = () => {
              const base64 = String(reader.result).split(',')[1] || '';
              resolve({
                filename: file.name.replace(/\.[^.]+$/, '') + '.jpg',
                contentType: 'image/jpeg',
                data: base64,
              });
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(blob);
          },
          'image/jpeg',
          quality,
        );
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error(`Could not load image: ${file.name}`));
      };
      img.src = objectUrl;
    });
  }

  buildProductSummary(productData) {
    const entries = Object.entries(productData).map(
      ([key, value]) => `${key}: ${value}`,
    );
    return entries.length ? entries.join('<br>') : 'Product added';
  }

  clearProductInputs(inputs) {
    inputs.forEach((input) => {
      input.value = '';
    });
  }

  updateTotalProducts() {
    const total = this.products.reduce((sum, product) => {
      const quantity = parseInt(product['quantity'] || '1', 10);
      return sum + quantity;
    }, 0);
    if (this.totalProductsEl) {
      this.totalProductsEl.textContent = `${total}`;
    }
  }

  handleSubmit = async (event) => {
    event.preventDefault();

    if (!this.submitEndpoint) {
      this.setStatus('Missing submission endpoint.', 'error');
      return;
    }

    this.setLoading(true);
    this.setStatus('', 'info');

    const formData = new FormData(this.form);

    if (this.productsForm) {
      const productInputs = Array.from(
        this.productsForm.querySelectorAll('[data-form-input]'),
      );
      productInputs.forEach((input) => {
        if (input.name) {
          formData.delete(input.name);
        }
      });
    }

    const payload = Object.fromEntries(formData.entries());
    payload.caseItems = this.products.map(({ id, ...rest }) => rest);
    // if (this.addProductButton && payload.caseItems.length === 0) {
    //   this.setStatus(
    //     'Please add at least one product before submitting.',
    //     'error',
    //   );
    //   this.setLoading(false);
    //   return;
    // }

    if (this.imageWidgets && this.imageWidgets.length) {
      for (const widget of this.imageWidgets) {
        if (widget.required && widget.files.length === 0) {
          this.setStatus(
            `Please add at least one image for ${widget.label}.`,
            'error',
          );
          this.setLoading(false);
          return;
        }
        if (widget.files.length) {
          try {
            payload[widget.name] = await this.buildImagePayload(widget);
          } catch (error) {
            console.error('Image processing error:', error);
            this.setStatus(
              'There was a problem processing your images. Please try again.',
              'error',
            );
            this.setLoading(false);
            return;
          }
        }
      }
    }
    try {
      const response = await fetch(this.submitEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Submission failed');
      }

      this.form.reset();
      this.products = [];
      if (this.productsList) {
        this.productsList.innerHTML = '';
      }
      this.resetImageWidgets();
      this.updateTotalProducts();
      this.setStatus('Form submitted successfully.', 'success');
    } catch (error) {
      console.error('Submission error:', error);
      this.setStatus('Something went wrong. Please try again.', 'error');
    } finally {
      this.setLoading(false);
    }
  };

  setFieldValidity(input, isValid) {
    if (isValid) {
      input.classList.remove('border-red-500');
      input.classList.add('border-border-neutral');
      input.removeAttribute('aria-invalid');
    } else {
      input.classList.add('border-red-500');
      input.setAttribute('aria-invalid', 'true');
    }
  }

  setLoading(isLoading) {
    if (this.loadingEl) {
      this.loadingEl.classList.toggle('hidden', !isLoading);
    }
    if (!this.submitButton) return;
    this.submitButton.disabled = isLoading;
    this.submitButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    if (this.submitButtonLabel) {
      this.submitButton.textContent = isLoading
        ? 'Submitting\u2026'
        : this.submitButtonLabel;
    }
  }

  setStatus(message, type) {
    if (!this.statusEl) return;
    if (!message) {
      this.statusEl.classList.add('hidden');
      this.statusEl.textContent = '';
      return;
    }

    this.statusEl.textContent = message;
    this.statusEl.classList.remove('hidden');
    this.statusEl.classList.remove('text-green-600', 'text-red-600');
    if (type === 'success') {
      this.statusEl.classList.add('text-green-600');
    }
    if (type === 'error') {
      this.statusEl.classList.add('text-red-600');
    }
  }
  autoFillWithURLParams() {
    const urlParams = new URLSearchParams(window.location.search);
    for (const [key, value] of urlParams) {
      if (key == 'productsInfo') {
        const values = JSON.parse(value);
        // need to check if values is an array.
        if (!Array.isArray(values)) continue;
        // add productIds
        this.products = values;
        continue;
      }
      const input = this.querySelector(`[name="${key}"]`);
      if (input) {
        input.value = value;
      }
    }
    if (!this.products || this.products.length === 0) return;
    // add product cards.
    this.renderProducts(this.products);
  }
  renderProducts(products) {
    if (!this.productsForm || !this.productTemplate || !this.productsList) {
      return;
    }
    for (const index in products) {
      const productData = products[index];
      this.addProductCard(productData, this.tabIndex);
    }

    this.updateTotalProducts();
    this.clearProductInputs(
      this.productsForm.querySelectorAll('[data-form-input]'),
    );
    this.setStatus('', 'info');
  }
  addProductCard(productData, productId) {
    if (!this.productsForm || !this.productTemplate || !this.productsList) {
      return;
    }
    const productNode = this.productTemplate.content.cloneNode(true);
    const summaryEl = productNode.querySelector('[data-product-summary]');
    const removeBtn = productNode.querySelector('[data-remove-product]');
    if (summaryEl) {
      summaryEl.innerHTML = this.buildProductSummary(productData);
    }
    if (removeBtn) {
      removeBtn.dataset.productId = productId;
      removeBtn.addEventListener('click', this.handleRemoveProduct);
    }
    this.productsList.appendChild(productNode);
  }
}
if (!customElements.get('tw-forms')) {
  customElements.define('tw-forms', TWForms);
}
